import { ExternalLink } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

type GameCard = {
  id: 'guess' | 'auction' | 'anime' | 'cards' | 'party' | 'sketch' | 'survivors';
  name: string;
  domain: string;
  href: string;
  imageSrc: string;
  badge?: string;
};

export function BrandCorner() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const currentGameId: GameCard['id'] = hostname.startsWith('guess.')
    ? 'guess'
    : hostname === 'anime-draft.com' || hostname === 'www.anime-draft.com'
      ? 'anime'
      : 'auction';

  const auctionHref = currentGameId === 'auction' ? '/' : 'https://auction.anime-draft.com';

  const games: GameCard[] = [
    {
      id: 'guess',
      name: 'Guess Who',
      domain: 'guess.anime-draft.com',
      href: 'https://guess.anime-draft.com',
      imageSrc: '/assets/Guess-Who.webp',
    },
    {
      id: 'auction',
      name: 'Auction Draft',
      domain: 'auction.anime-draft.com',
      href: auctionHref,
      imageSrc: '/assets/Auction Draft.webp',
    },
    {
      id: 'anime',
      name: 'Anime-Draft',
      domain: 'anime-draft.com',
      href: 'https://anime-draft.com',
      imageSrc: '/assets/Anime-Draft.webp',
    },
    {
      id: 'cards',
      name: 'Anime-Draft Cards',
      domain: 'cards.anime-draft.com',
      href: 'https://cards.anime-draft.com',
      imageSrc: '/assets/Anime-Draft Cards.webp',
    },
    {
      id: 'party',
      name: 'Anime-Draft Party',
      domain: 'party.anime-draft.com',
      href: 'https://party.anime-draft.com',
      imageSrc: '/assets/Anime-Draft Party.webp',
    },
    {
      id: 'sketch',
      name: 'Anime-Draft Sketch',
      domain: 'sketch.anime-draft.com',
      href: 'https://sketch.anime-draft.com',
      imageSrc: '/assets/Anime-Draft Sketch.webp',
      badge: 'New',
    },
  ];

  return (
    <div className="fixed right-2 top-2 z-50 sm:left-0 sm:right-auto sm:p-2">
      <div className="flex items-center gap-3">
        <a href="/" aria-label="Go to home" className="hidden sm:block">
          <img
            src="/logo.png"
            alt="Anime-Draft Auction logo"
            className="h-10 w-10 md:h-12 md:w-12 drop-shadow-[0_8px_16px_rgba(0,0,0,0.35)]"
          />
        </a>
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="font-display border-primary/50 bg-card text-primary hover:bg-secondary hover:border-primary hover:text-primary">
              More Games
            </Button>
          </DialogTrigger>
          <DialogContent className="overflow-hidden border-border bg-card p-0 text-foreground sm:max-w-[980px] [&>button]:right-3 [&>button]:top-3 [&>button]:inline-flex [&>button]:h-8 [&>button]:w-8 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-md [&>button]:border [&>button]:border-border [&>button]:bg-secondary [&>button]:p-0 [&>button]:text-foreground [&>button]:leading-none [&>button]:opacity-100 [&>button]:hover:bg-muted">
            <div className="border-t-2 border-primary p-3 sm:p-4">
              <DialogHeader className="space-y-1.5 text-left">
                <DialogTitle className="font-display text-xl leading-none text-foreground sm:text-2xl">More Games</DialogTitle>
                <p className="text-xs text-muted-foreground sm:text-sm">Other games in the Anime-Draft series. They open in a new tab.</p>
              </DialogHeader>

              <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
                {games.map((game) => {
                  const isCurrent = game.id === currentGameId;

                  return (
                    <a
                      key={game.id}
                      href={game.href}
                      target={isCurrent ? undefined : '_blank'}
                      rel={isCurrent ? undefined : 'noreferrer'}
                      aria-current={isCurrent ? 'page' : undefined}
                      className={[
                        'group relative isolate block aspect-[16/8.6] overflow-hidden rounded-lg border-2 bg-background transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card',
                        isCurrent ? 'border-primary' : 'border-border hover:border-primary/60',
                      ].join(' ')}
                    >
                      <img
                        src={game.imageSrc}
                        alt={`${game.name} cover`}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />

                      {(isCurrent || game.badge) && (
                        <div className="absolute right-2 top-2">
                          <div
                            className={[
                              'rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]',
                              isCurrent ? 'bg-primary text-primary-foreground' : 'bg-player text-player-foreground',
                            ].join(' ')}
                          >
                            {isCurrent ? 'You are here' : game.badge}
                          </div>
                        </div>
                      )}

                      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 border-t border-border bg-background px-3 py-2">
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate font-display text-sm leading-tight text-foreground sm:text-base">{game.name}</span>
                          <span className="truncate text-xs text-muted-foreground">{game.domain}</span>
                        </div>
                        {!isCurrent && (
                          <ExternalLink className="mb-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                        )}
                      </div>
                    </a>
                  );
                })}
              </div>

              <DialogClose asChild>
                <button
                  type="button"
                  className="mt-3 w-full rounded-lg border border-border bg-secondary px-4 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                >
                  Close
                </button>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
